import React, {useCallback, useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    EyeIcon,
    MagnifyingGlassIcon,
    PlusCircleIcon,
    FunnelIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    XMarkIcon,
} from '@heroicons/react/24/solid';
import CustomerAutoComplete from '../../customers/components/CustomerAutoComplete';
import {createOrderRequest, searchOrderRequest} from "../../../store/order/actions/order-actions";
import {getCustomerRequest} from "../../../store/customer/actions/get-customer-actions";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import UserSelect from '../../../components/UserSelect';

const OrderSearchSection = ({data, searchResults, page, size, handlePageChange}) => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();

    const defaultSearchParams = {
        status: '',
        discountMin: '',
        discountMax: '',
        createdAfter: '',
        createdBefore: '',
        updatedAfter: '',
        updatedBefore: '',
        createdBy: '',
        updatedBy: '',
        customerId: '',
    };

    const {t} = useTranslation()
    const dispatch = useDispatch();
    // OrderSearchPage search state

    // Initialize from URL params
    const [searchParams, setSearchParams] = useState({
        status: urlSearchParams.get('status') || '',
        discountMin: urlSearchParams.get('discountMin') || '',
        discountMax: urlSearchParams.get('discountMax') || '',
        createdAfter: urlSearchParams.get('createdAfter') || '',
        createdBefore: urlSearchParams.get('createdBefore') || '',
        updatedAfter: urlSearchParams.get('updatedAfter') || '',
        updatedBefore: urlSearchParams.get('updatedBefore') || '',
        createdBy: urlSearchParams.get('createdBy') || '',
        updatedBy: urlSearchParams.get('updatedBy') || '',
        customerId: urlSearchParams.get('customerId') || '',
    });

    const INSTANT_FIELDS = ['createdAfter', 'createdBefore', 'updatedAfter', 'updatedBefore'];
    const {order, loading} = useSelector(state => state.orderData);
    const {customer: fetchedCustomer} = useSelector(state => state.getCustomer);
    const users = useSelector(state => state.user?.users || []);

    // Track previous filters to detect changes
    const prevFiltersRef = useRef(null);
    const isInitialMount = useRef(true);
    
    // State for selected customer (to populate autocomplete)
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // -------------------- ORDER SEARCH --------------------
    const handleSearchParamsChange = useCallback((field, value) => {
        setSearchParams((prev) => ({...prev, [field]: value}));
    }, []);


    const navigate = useNavigate();

    const getFilteredParams = () => {
        return Object.fromEntries(
            Object.entries(searchParams)
                .filter(([key, value]) =>
                    value !== '' &&
                    value != null &&
                    value !== defaultSearchParams[key] // Only if changed from default
                )
                .map(([key, value]) => [
                    key,
                    INSTANT_FIELDS.includes(key) && value ? `${value}:00Z` : value
                ])
        );
    };

    // Load data from URL on mount and when URL changes
    useEffect(() => {
        const filteredParams = getFilteredParams();
        
        // If customerId in URL, fetch customer details
        if (urlSearchParams.get('customerId')) {
            dispatch(getCustomerRequest(urlSearchParams.get('customerId')));
        } else {
            setSelectedCustomer(null); // Clear customer if not in URL
        }
        
        const hasFilters = Object.keys(filteredParams).length > 0;
        
        if (isInitialMount.current) {
            // On initial mount (page load/refresh) - always trigger search
            isInitialMount.current = false;
            prevFiltersRef.current = filteredParams;
            
            // Trigger search with filters from URL (or empty if no filters)
            dispatch(searchOrderRequest(filteredParams, {page: page, size: size}));
        } else {
            // Component already mounted - check if URL was cleared (sidebar click)
            if (!hasFilters && page === 0 && Object.keys(prevFiltersRef.current || {}).length > 0) {
                // URL was cleared (had filters, now has none) - reset state and trigger default search
                prevFiltersRef.current = {};
                setSearchParams(defaultSearchParams); // Reset filter inputs to defaults
                setSelectedCustomer(null); // Clear customer
                dispatch(searchOrderRequest({}, {page: 0, size: size}));
            }
        }
    }, [urlSearchParams]);

    // When customer is fetched from API, set it as selected
    useEffect(() => {
        if (fetchedCustomer) {
            setSelectedCustomer(fetchedCustomer);
        }
    }, [fetchedCustomer]);

    const handleSearch = useCallback((e) => {
        e.preventDefault();
        
        const filteredParams = getFilteredParams();

        // Check if filters changed by comparing with previous filters
        const filtersChanged = JSON.stringify(filteredParams) !== JSON.stringify(prevFiltersRef.current);
        const targetPage = filtersChanged ? 0 : page;

        // Store current filters for next comparison
        prevFiltersRef.current = filteredParams;

        // Update URL with all current filters and target page
        const newUrlParams = new URLSearchParams();
        Object.entries(filteredParams).forEach(([key, value]) => {
            newUrlParams.set(key, value);
        });
        newUrlParams.set('page', targetPage.toString());
        newUrlParams.set('size', size.toString());
        setUrlSearchParams(newUrlParams);

        // Dispatch search (URL already updated above)
        dispatch(searchOrderRequest(filteredParams, {page: targetPage, size: size}));
    }, [searchParams, dispatch, page, size, setUrlSearchParams]);

    const handleClearFilters = useCallback(() => {
        // Reset all filters to default
        setSearchParams(defaultSearchParams);
        setSelectedCustomer(null);
        prevFiltersRef.current = {};
        
        // Update URL with only page and size
        const newUrlParams = new URLSearchParams();
        newUrlParams.set('page', '0');
        newUrlParams.set('size', size.toString());
        setUrlSearchParams(newUrlParams);
        
        // Trigger search with empty filters
        dispatch(searchOrderRequest({}, {page: 0, size: size}));
    }, [size, setUrlSearchParams, dispatch]);

    const handleSelectOrder = (orderId) => {
        navigate(`/orders/${orderId}`, {
            state: {
                from: {
                    pathname: location.pathname,
                    search: location.search,
                }
            }
        });
    }

    const handleCreateDefaultOrder = useCallback(() => {
        dispatch(createOrderRequest());
        if (order) {
            handleSelectOrder(order.id);
        }
    }, [dispatch, order]);

    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    const handleInputChange = (field, value) => {
        handleSearchParamsChange(field, value);
    };

    // When a customer is selected, update state (but don't trigger search or update URL)
    const handleCustomerSelect = (customer) => {
        // Update selected customer state
        setSelectedCustomer(customer);
        
        // Update search params state
        setSearchParams(prev => ({
            ...prev,
            customerId: customer ? customer.id : ''
        }));
    };

    const handlePreviousPage = () => {
        if (page > 0) {
            const newPage = page - 1;
            const filteredParams = getFilteredParams();

            // Update URL with filters
            const newUrlParams = new URLSearchParams();
            Object.entries(filteredParams).forEach(([key, value]) => {
                newUrlParams.set(key, value);
            });
            newUrlParams.set('page', newPage.toString());
            newUrlParams.set('size', size.toString());
            setUrlSearchParams(newUrlParams);

            // Dispatch search with filters (URL already updated above)
            dispatch(searchOrderRequest(filteredParams, {page: newPage, size: size}));
        }
    };

    const handleNextPage = () => {
        if (page < data.totalPages) {
            const newPage = page + 1;
            const filteredParams = getFilteredParams();

            // Update URL with filters
            const newUrlParams = new URLSearchParams();
            Object.entries(filteredParams).forEach(([key, value]) => {
                newUrlParams.set(key, value);
            });
            newUrlParams.set('page', newPage.toString());
            newUrlParams.set('size', size.toString());
            setUrlSearchParams(newUrlParams);

            // Dispatch search with filters (URL already updated above)
            dispatch(searchOrderRequest(filteredParams, {page: newPage, size: size}));
        }
    };

    return (
        <div className="mb-8 bg-white shadow rounded-lg p-6">
            <h2 className="text-2xl font-semibold mb-4">{t('order.searchOrders')}</h2>

            {/* Always visible filters */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Status Dropdown */}
                <div>
                    <label className="block text-sm font-medium">{t('common.status')}</label>
                    <select
                        value={searchParams.status || ''}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="border p-2 rounded w-full"
                    >
                        <option value="">{t('common.select')}</option>
                        <option value="OPEN">{t('order.status.open')}</option>
                        <option value="IN_PROGRESS">{t('order.status.inProgress')}</option>
                        <option value="DONE">{t('order.status.done')}</option>
                        <option value="CLOSED">{t('order.status.closed')}</option>
                    </select>
                </div>

                {/* Customer Autocomplete */}
                <div>
                    <label className="block text-sm font-medium">{t('order.customer')}</label>
                    <CustomerAutoComplete
                        value={selectedCustomer ? (selectedCustomer.name || `${selectedCustomer.firstName} ${selectedCustomer.lastName}`) : ''}
                        onChange={handleCustomerSelect}
                    />
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mb-4">
                <button
                    onClick={handleSearch}
                    type="button"
                    title={t('order.searchOrders')}
                    className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded transition"
                    style={{backgroundColor: "#34495e"}}

                >
                    <MagnifyingGlassIcon className="h-5 w-5"/>
                </button>

                <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded transition"
                    title={t('common.filters')}
                >
                    <FunnelIcon className="h-5 w-5"/>
                    {showAdvancedFilters ? (
                        <ChevronUpIcon className="h-5 w-5"/>
                    ) : (
                        <ChevronDownIcon className="h-5 w-5"/>
                    )}
                </button>


                <button
                    onClick={handleClearFilters}
                    type="button"
                    title={t('common.clear')}
                    className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition"
                >
                    <XMarkIcon className="h-5 w-5"/>
                </button>
            </div>

            {/* Collapsible Advanced Filters */}
            {showAdvancedFilters && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Created After / Before */}
                    <div>
                        <label className="block text-sm font-medium">{t('common.createdAt')} {t('common.after')}</label>
                        <input
                            type="datetime-local"
                            value={searchParams.createdAfter || ''}
                            onChange={(e) => handleInputChange('createdAfter', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">{t('common.createdAt')} {t('common.before')}</label>
                        <input
                            type="datetime-local"
                            value={searchParams.createdBefore || ''}
                            onChange={(e) => handleInputChange('createdBefore', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>

                    {/* Updated After / Before */}
                    <div>
                        <label className="block text-sm font-medium">{t('common.updatedAt')} {t('common.after')}</label>
                        <input
                            type="datetime-local"
                            value={searchParams.updatedAfter || ''}
                            onChange={(e) => handleInputChange('updatedAfter', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">{t('common.updatedAt')} {t('common.before')}</label>
                        <input
                            type="datetime-local"
                            value={searchParams.updatedBefore || ''}
                            onChange={(e) => handleInputChange('updatedBefore', e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>

                    {/* Created By and Updated By */}
                    <div>
                        <label className="block text-sm font-medium">{t('common.createdBy')}</label>
                        <UserSelect
                            value={searchParams.createdBy || ''}
                            onChange={(value) => handleInputChange('createdBy', value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">{t('common.updatedBy')}</label>
                        <UserSelect
                            value={searchParams.updatedBy || ''}
                            onChange={(value) => handleInputChange('updatedBy', value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>
                </div>
            )}

            <div>
                {searchResults && searchResults.length > 0 ? (
                    <>
                        <table className="min-w-full divide-y divide-gray-200 shadow-md rounded-md overflow-hidden">
                            <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-600">
                            <tr>
                                <th className="px-6 py-3">ID</th>
                                <th className="px-6 py-3">{t('order.customer')}</th>
                                <th className="px-6 py-3">{t('common.status')}</th>
                                <th className="px-6 py-3">{t('common.createdAt')}</th>
                                <th className="px-6 py-3">{t('common.createdBy')}</th>
                                <th className="px-6 py-3 text-center">{t('common.actions')}</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100 text-sm text-gray-800">
                            {searchResults.map((order) => (
                                <tr key={order.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4">{order.id}</td>
                                    <td className="px-6 py-4">{customerName(order.customer)}</td>
                                    <td className="px-6 py-4">{order.status}</td>
                                    <td className="px-6 py-4">{order.createdAt}</td>
                                    <td className="px-6 py-4">{getCreatedByFullName(order, users)}</td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => handleSelectOrder(order.id)}
                                            className="inline-flex items-center justify-center p-2 rounded-full text-blue-600 hover:text-white hover:bg-blue-600 transition duration-200"
                                            title={t('order.orderDetails')}
                                        >
                                            <EyeIcon className="h-5 w-5"/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        {data.totalPages > 1 && (
                            <div className="flex justify-between items-center mt-4">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={page < 1}
                                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                                    style={{backgroundColor: '#34495e'}}
                                >
                                    {t('pagination.previous')}
                                </button>
                                <span>
                        {t('pagination.page')} {page + 1} {t('pagination.of')} {data.totalPages}
                    </span>
                                <button
                                    onClick={handleNextPage}
                                    disabled={page >= data.totalPages - 1}
                                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                                    style={{backgroundColor: '#34495e'}}
                                >
                                    {t('pagination.next')}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-gray-600">{t('order.noOrders')}</p>
                )}
            </div>
            <div className="pt-8">
                <button
                    onClick={handleCreateDefaultOrder}
                    title={t('order.createOrder')}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition"
                    style={{backgroundColor: "#34495e"}}
                >
                    <PlusCircleIcon className="h-5 w-5"/>
                    {t('order.createOrder')}
                </button>
            </div>
        </div>
    );
};

const customerName = (customer) => {
    if (customer) {
        return customer.name ? customer.name : customer.firstName + ' ' + customer.lastName;
    }
    return '/';
}

const getCreatedByFullName = (order, users) => {
    if (!order.createdAt || !order.createdByFullName) return 'N/A';
    const user = users.find(u => u.id === order.createdAt);
    if (user) {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || 'N/A';
    }
    if(order.createdByFullName) {
        return order.createdByFullName;
    }
    return 'N/A';
}

export default OrderSearchSection;
